(() => {
  const READ_MORE_MOBILE_QUERY = '(max-width: 699px)';
  const instances = new Set();
  const mediaQuery = window.matchMedia(READ_MORE_MOBILE_QUERY);
  let frame = null;
  let isWatchingLayout = false;

  const scheduleUpdates = () => {
    if (frame) return;

    frame = requestAnimationFrame(() => {
      frame = null;
      instances.forEach((instance) => instance.update());
    });
  };

  const addMediaQueryListener = () => {
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', scheduleUpdates);
      return;
    }

    mediaQuery.addListener(scheduleUpdates);
  };

  const removeMediaQueryListener = () => {
    if (typeof mediaQuery.removeEventListener === 'function') {
      mediaQuery.removeEventListener('change', scheduleUpdates);
      return;
    }

    mediaQuery.removeListener(scheduleUpdates);
  };

  const watchLayout = () => {
    if (isWatchingLayout) return;

    window.addEventListener('resize', scheduleUpdates);
    addMediaQueryListener();
    isWatchingLayout = true;
  };

  const unwatchLayout = () => {
    if (instances.size || !isWatchingLayout) return;

    window.removeEventListener('resize', scheduleUpdates);
    removeMediaQueryListener();
    isWatchingLayout = false;

    if (frame) {
      cancelAnimationFrame(frame);
      frame = null;
    }
  };

  if (!customElements.get('read-more-text')) {
    class ReadMoreText extends HTMLElement {
      connectedCallback() {
        this.setProperties();

        if (!this.content || !this.toggle) return;

        this.expanded = this.expanded || false;
        this.setEventListeners();
        instances.add(this);
        watchLayout();
        this.update();
      }

      disconnectedCallback() {
        this.removeEventListeners();
        instances.delete(this);
        unwatchLayout();
      }

      setProperties() {
        this.content = this.querySelector('[data-read-more-content]');
        this.toggle = this.querySelector('[data-read-more-toggle]');
        this.moreLabel = this.querySelector('[data-read-more-label-more]');
        this.lessLabel = this.querySelector('[data-read-more-label-less]');
        this.lineLimit = Math.max(1, Number.parseInt(this.dataset.readMoreLineLimit, 10) || 10);
      }

      setEventListeners() {
        if (this.isListening) return;

        this.handleToggle = this.handleToggle || this.onToggle.bind(this);
        this.toggle.addEventListener('click', this.handleToggle);
        this.isListening = true;
      }

      removeEventListeners() {
        if (!this.isListening) return;

        this.toggle?.removeEventListener('click', this.handleToggle);
        this.isListening = false;
      }

      update() {
        this.clearTruncation();

        // Desktop: always show the full text and hide the toggle.
        if (!mediaQuery.matches) {
          this.classList.remove('read-more--collapsed', 'read-more--expanded');
          this.toggle.hidden = true;
          this.setLabelState(false);
          return;
        }

        if (!this.prepareTruncation()) {
          this.classList.remove('read-more--collapsed', 'read-more--expanded');
          this.toggle.hidden = true;
          this.setLabelState(false);
          return;
        }

        this.toggle.hidden = false;
        this.setState(this.expanded);
      }

      clearTruncation() {
        Array.from(this.content.children).forEach((element) => {
          element.removeAttribute('data-read-more-clamp');
          element.removeAttribute('data-read-more-hidden');
        });
      }

      prepareTruncation() {
        const elements = Array.from(this.content.children);
        let visibleLines = 0;
        let hasVisibleElement = false;

        for (const [index, element] of elements.entries()) {
          const elementLines = this.getLineCount(element);
          const startsNewParagraph = element.tagName === 'P' && hasVisibleElement;

          if (startsNewParagraph && visibleLines === this.lineLimit - 1) {
            this.hideElementsFrom(elements, index);
            return true;
          }

          const remainingLines = this.lineLimit - visibleLines;

          if (remainingLines <= 0) {
            this.hideElementsFrom(elements, index);
            return true;
          }

          if (elementLines > remainingLines) {
            element.setAttribute('data-read-more-clamp', String(remainingLines));
            this.hideElementsFrom(elements, index + 1);
            return true;
          }

          visibleLines += elementLines;
          hasVisibleElement = true;

          if (visibleLines >= this.lineLimit && index < elements.length - 1) {
            this.hideElementsFrom(elements, index + 1);
            return true;
          }
        }

        return false;
      }

      hideElementsFrom(elements, startIndex) {
        elements.slice(startIndex).forEach((element) => {
          element.setAttribute('data-read-more-hidden', '');
        });
      }

      getLineCount(element) {
        const computedStyle = window.getComputedStyle(element);
        const lineHeight = Number.parseFloat(computedStyle.lineHeight);
        const elementHeight = element.getBoundingClientRect().height;

        if (element.tagName === 'P' && Number.isFinite(lineHeight) && lineHeight > 0) {
          return Math.max(1, Math.round(elementHeight / lineHeight));
        }

        const range = document.createRange();
        range.selectNodeContents(element);

        const linePositions = [];

        Array.from(range.getClientRects()).forEach((rect) => {
          if (rect.width <= 0 || rect.height <= 0) return;

          const sameLine = linePositions.some((position) => {
            return Math.abs(position - rect.top) < Math.max(1, rect.height * 0.5);
          });

          if (!sameLine) linePositions.push(rect.top);
        });

        range.detach?.();

        return Math.max(1, linePositions.length);
      }

      onToggle() {
        this.setState(!this.expanded);
      }

      setState(expanded) {
        this.expanded = expanded;
        this.classList.toggle('read-more--collapsed', !expanded);
        this.classList.toggle('read-more--expanded', expanded);
        this.setLabelState(expanded);
      }

      setLabelState(expanded) {
        this.toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (this.moreLabel) this.moreLabel.hidden = expanded;
        if (this.lessLabel) this.lessLabel.hidden = !expanded;
      }
    }

    customElements.define('read-more-text', ReadMoreText);
  }
})();
